import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../api";
import { Modal, Field, TextArea, Selecter } from "./crmui";
import { Btn } from "../../components/common";

const EMPTY = {
  tipo: "nota", assunto: "", corpo: "", contact_id: "", contact_ids: [],
  resultado: "", followup_dt: "", followup_notas: "",
};

/**
 * Modal partilhado de registo de atividade CRM.
 * Props:
 *   open          boolean
 *   onClose       () => void
 *   accountId     string (opcional - vazio para atividade sem conta)
 *   accountNome   string
 *   accountNif    string
 *   contacts      array de { id, nome, email, departamento, principal }
 *   onSuccess     () => void  (chamado apos guardar com sucesso)
 *   onNewContact  () => void  (opcional - botao "+ Novo contacto")
 */
export default function ActivityModal({
  open, onClose,
  accountId = "", accountNome = "", accountNif = "",
  contacts = [],
  onSuccess,
  onNewContact,
}) {
  const [act, setAct] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const reset = () => setAct(EMPTY);

  const handleClose = () => { reset(); onClose(); };

  const save = async () => {
    setBusy(true);
    try {
      const cids = act.contact_ids?.length ? act.contact_ids : (act.contact_id ? [act.contact_id] : []);
      let followup_dt = "";
      if (act.tipo === "chamada" && act.followup_dt && act.resultado && act.resultado !== "atendeu") {
        try { followup_dt = new Date(act.followup_dt).toISOString(); } catch { followup_dt = act.followup_dt; }
      }
      const payload = { ...act, account_id: accountId, contact_id: cids[0] || "", contact_ids: cids, followup_dt };
      const { data } = await api.post("/crm/activities", payload);
      if (data?.created_task) {
        toast.success(`Atividade registada + recontacto agendado para ${(data.created_task.prazo || "").slice(0, 16).replace("T", " ")}.`);
      } else {
        toast.success("Atividade registada.");
      }
      reset();
      onClose();
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao registar atividade.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Registar atividade" testid="activity-modal">
      {accountNome && (
        <div className="mb-3" data-testid="activity-account-chip">
          <label className="block text-xs uppercase text-muted2 mb-1">Conta</label>
          <div className="flex items-center gap-2 bg-blue/5 border border-blue/20 rounded-md px-3 py-2">
            <span className="text-navy font-semibold text-sm flex-1">{accountNome}</span>
            {accountNif && <span className="text-[11px] text-muted2">NIF {accountNif}</span>}
          </div>
        </div>
      )}

      <Selecter label="Tipo" value={act.tipo} onChange={(v) => setAct({ ...act, tipo: v })}
        options={["nota", "chamada", "email", "reuniao", "demo", "tarefa"]} testid="activity-tipo" />

      {contacts.length > 0 && (
        <div className="mb-3" data-testid="activity-contact-picker">
          <label className="flex items-center justify-between text-xs uppercase text-muted2 mb-1">
            <span>Contactos {act.contact_ids?.length > 0 && <span className="text-blue">({act.contact_ids.length} sel.)</span>}</span>
            {onNewContact && (
              <button type="button" onClick={() => { onClose(); setTimeout(onNewContact, 100); }}
                data-testid="activity-new-contact-btn"
                className="text-[11px] font-semibold text-blue hover:underline">
                + Novo contacto
              </button>
            )}
          </label>
          <div className="max-h-36 overflow-y-auto border border-tint rounded-md divide-y divide-slate-100">
            {contacts.map((c) => {
              const checked = (act.contact_ids || []).includes(c.id);
              return (
                <label key={c.id} className="flex items-center gap-2 px-2.5 py-1.5 text-[13px] cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={checked}
                    data-testid={`activity-contact-cb-${c.id}`}
                    onChange={(e) => {
                      const next = new Set(act.contact_ids || []);
                      if (e.target.checked) next.add(c.id); else next.delete(c.id);
                      const arr = Array.from(next);
                      setAct({ ...act, contact_ids: arr, contact_id: arr[0] || "" });
                    }}
                    className="rounded border-slate-300" />
                  <span className="text-navy font-medium">{c.nome}</span>
                  {c.departamento && <span className="text-[10px] text-slate-500 uppercase">{c.departamento}</span>}
                  {c.principal && <span className="text-[10px] text-emerald-600">*</span>}
                  {c.email && <span className="text-[11px] text-slate-500 ml-auto truncate">{c.email}</span>}
                </label>
              );
            })}
          </div>
        </div>
      )}

      <Field label="Assunto" value={act.assunto} onChange={(v) => setAct({ ...act, assunto: v })} testid="activity-assunto" />
      <TextArea label="Detalhe" value={act.corpo} onChange={(v) => setAct({ ...act, corpo: v })} testid="activity-corpo" />

      {act.tipo === "chamada" && (
        <div className="border border-amber-200 bg-amber-50/60 rounded-md p-3 mt-2 space-y-2" data-testid="activity-call-panel">
          <label className="block text-xs uppercase text-muted2 mb-1">Resultado da chamada</label>
          <select value={act.resultado || ""} onChange={(e) => setAct({ ...act, resultado: e.target.value })}
            data-testid="activity-resultado"
            className="w-full px-3 py-2 border border-tint rounded-lg text-sm bg-white">
            <option value="">- Seleccione -</option>
            <option value="atendeu">Atendeu (sem follow-up)</option>
            <option value="contactar_mais_tarde">Atendeu - pediu para ligar mais tarde</option>
            <option value="pessoa_indisponivel">Atendeu - pessoa responsavel nao disponivel</option>
            <option value="nao_atendeu">Nao atendeu</option>
            <option value="caixa_voz">Caixa de voz</option>
            <option value="ocupado">Linha ocupada</option>
            <option value="numero_errado">Numero errado</option>
          </select>
          {act.resultado && act.resultado !== "atendeu" && (
            <div className="space-y-2 pt-2 border-t border-amber-200">
              <div className="text-[12px] text-amber-900">
                {act.resultado === "contactar_mais_tarde"
                  ? "O cliente pediu para ligares mais tarde. Agenda o novo contacto."
                  : "Chamada nao foi concluida. Agenda tarefa de recontacto."}
              </div>
              <Field label="Data e hora do recontacto" type="datetime-local"
                value={act.followup_dt || ""} onChange={(v) => setAct({ ...act, followup_dt: v })}
                testid="activity-followup-dt" />
              <TextArea label="A fazer (opcional)" value={act.followup_notas || ""}
                onChange={(v) => setAct({ ...act, followup_notas: v })}
                testid="activity-followup-notas" />
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Btn variant="ghost" onClick={handleClose}>Cancelar</Btn>
        <Btn onClick={save} disabled={busy} testid="activity-save">{busy ? "A guardar..." : "Registar"}</Btn>
      </div>
    </Modal>
  );
}
